import * as THREE from 'three';
import type { CharacterRig } from './build';

/**
 * Procedurele animatie voor personages.
 *
 * Er zijn geen animatieclips. De loopcyclus is een functie van de cyclusfase; idle is een
 * trage ademhaling; de aim-pose draait de romp en de schietarm naar het richtpunt. Alles is
 * een functie van tijd en snelheid, dus het schaalt vanzelf mee met hoe hard iemand loopt.
 *
 * Werkt op twee soorten rig met dezelfde code: de procedurele uit `build.ts` en het
 * geskinde HD-model uit `loadRig.ts`. Rotaties worden absoluut gezet, wat mag omdat beide
 * rigs in rust op identity staan — zie `docs/CHARACTERS.md`.
 *
 * ROTATIECONVENTIE (gemeten aan de rig, niet afgeleid uit de code)
 * Het personage kijkt langs +Z. Voor heup en schouder geldt: `rotation.x` negatief is naar
 * voren, positief naar achteren. Voor de knie is `rotation.x` positief de enige toegestane
 * richting — dat brengt de hiel naar de bil. De elleboog is precies andersom: negatief,
 * want een elleboog buigt naar voren en een knie naar achteren. Die asymmetrie is echt en
 * geen slordigheid.
 */

/** Volledige loopcycli per seconde op referentiesnelheid. Eén cyclus is twee passen. */
const STRIDE_FREQUENCY = 1.55;

/** Maximale uitslag van heup en schouder in radialen. */
const HIP_SWING = 0.62;
const SHOULDER_SWING = 0.5;
const ELBOW_BEND = 0.4;
const SEGMENTED_HIP_SWING = 0.36;
const SEGMENTED_SHOULDER_SWING = 0.28;
const SEGMENTED_ELBOW_BEND = 0.28;
const SEGMENTED_KNEE_SCALE = 0.62;

/**
 * Kniebuiging in radialen, altijd positief: een knie scharniert maar één kant op.
 *
 * De cyclus loopt van hielneerzet (t = 0) via afzet (t ≈ 0.6) naar de volgende hielneerzet.
 * Er zitten twee buigingen in, en dat is precies wat een looppas leesbaar maakt:
 * een kleine dempingsbuiging net na de hielneerzet, en een grote in de vroege zwaaifase
 * die de voet vrij van de grond tilt. Daartussen is het been vrijwel gestrekt terwijl het
 * lichaam er overheen rolt.
 */
const KNEE_BASE = 0.05; // ~3°, nooit helemaal op slot
const KNEE_SWING_FLEX = 1.05; // ~60°, voetvrijheid in de zwaaifase

/**
 * Demping na de hielneerzet. Anatomisch hoort hier 15 à 20 graden, maar de rig heeft geen
 * enkel: de voet zit vast aan het onderbeen en kantelt dus mee. Bij 13 graden boorde de
 * teen 2,5 cm de stoep in. Op 8 graden blijft de demping zichtbaar en blijft de voet
 * boven de grond.
 */
const KNEE_STANCE_FLEX = 0.14;

/** Waar in de cyclus die twee buigingen hun piek hebben, als fractie van de cyclus. */
const KNEE_STANCE_AT = 0.15;
const KNEE_SWING_AT = 0.73;
const KNEE_STANCE_WIDTH = 0.2;
const KNEE_SWING_WIDTH = 0.22;

/**
 * Hoeveel het bekken zakt op het diepste punt van de pas.
 *
 * Meetkundig gedwongen, niet vrij te kiezen: bij een heupuitslag van 0,62 rad reikt het
 * gestrekte been in de spreidstand een stuk minder ver naar beneden dan rechtop. Zakt het
 * bekken daar niet mee, dan zweeft de voet — met de oude 0,035 was dat bijna 6 cm.
 */
const PELVIS_DIP = 0.075;
const SEGMENTED_PELVIS_DIP = 0.035;
const SEGMENTED_FOOT_CLEARANCE = 0.019;

/** Loopsnelheid waarbij de cyclus op volle uitslag draait. */
const REFERENCE_SPEED = 3.2;

/** Rusthouding van het hoofd: iets naar voren gekanteld, iets gedraaid. Radialen. */
const HEAD_REST_TILT = 0.06;
const HEAD_REST_TURN = 0.1;

const TAU = Math.PI * 2;

export type Pose = 'idle' | 'walk';

/**
 * Eén gladde, periodieke puls: 1 in het midden, 0 op `centre ± halfWidth`, daarbuiten nul.
 * `t` en `centre` zijn cyclusfracties, dus de puls loopt netjes over de naad bij t = 1.
 */
function pulse(t: number, centre: number, halfWidth: number): number {
  let delta = t - centre;
  delta -= Math.round(delta); // wikkel naar [−0.5, 0.5)
  const x = Math.abs(delta) / halfWidth;
  return x >= 1 ? 0 : 0.5 * (1 + Math.cos(Math.PI * x));
}

/** Kniebuiging op cyclusfractie `t`. Altijd ≥ 0; de knie buigt nooit naar voren. */
export function kneeFlexion(t: number): number {
  return (
    KNEE_BASE +
    KNEE_STANCE_FLEX * pulse(t, KNEE_STANCE_AT, KNEE_STANCE_WIDTH) +
    KNEE_SWING_FLEX * pulse(t, KNEE_SWING_AT, KNEE_SWING_WIDTH)
  );
}

/** Heupbuiging op cyclusfractie `t`: positief is naar voren, maximaal bij de hielneerzet. */
export function hipFlexion(t: number): number {
  return HIP_SWING * Math.cos(TAU * t);
}

export class CharacterAnimator {
  #phase = 0;
  #aimBlend = 0;
  #currentSpeed = 0;

  constructor(private readonly rig: CharacterRig) {}

  /**
   * @param dt        vaste simulatiestap in seconden
   * @param speed     horizontale snelheid in m/s
   * @param aiming    richt het personage zijn wapen?
   * @param aimPitch  hoek van de schietarm ten opzichte van horizontaal, in radialen
   */
  update(dt: number, speed: number, aiming: boolean, aimPitch = 0): void {
    // Snelheid gladstrijken, anders klapt de cyclus bij elke richtingswissel dicht.
    this.#currentSpeed += (speed - this.#currentSpeed) * Math.min(1, dt * 12);

    const intensity = Math.min(1, this.#currentSpeed / REFERENCE_SPEED);
    const pose: Pose = intensity > 0.05 ? 'walk' : 'idle';

    // Binnen één cyclus houden: de looppose leest de fase als fractie, en dan gaat na een
    // half uur spelen de precisie eraf.
    this.#phase = (this.#phase + dt * STRIDE_FREQUENCY * TAU * Math.max(intensity, 0.001)) % TAU;
    this.#aimBlend += ((aiming ? 1 : 0) - this.#aimBlend) * Math.min(1, dt * 10);

    if (pose === 'walk') {
      this.#applyWalk(intensity);
    } else {
      this.#applyIdle();
    }

    this.#applyAim(aimPitch);
  }

  /** Zet alle gewrichten terug op de rustpose. */
  reset(): void {
    for (const joint of Object.values(this.rig.joints)) {
      joint.rotation.set(0, 0, 0);
    }
    this.rig.joints.pelvis.position.y = this.rig.spec.proportions.hipHeight;
  }

  /**
   * De loopcyclus draait op beide rigs op volle uitslag. Er heeft hier een demping van
   * 0.45 op de geskinde rig gestaan; die is eruit omdat hij niet nodig bleek. Het
   * HD-model vervormt netjes bij zowel 63 graden kniebuiging als 35 graden heupuitslag —
   * gecontroleerd in de twee zwaarste poses van de cyclus. De demping halveerde alleen de
   * pas, en dat was juist het probleem. De aim-pose heeft wél eigen waarden per rig; die
   * staan in `#applyAim`.
   */
  #applyWalk(intensity: number): void {
    const { joints } = this.rig;
    const segmented = this.rig.deformation === 'segmented';
    const hipSwing = segmented ? SEGMENTED_HIP_SWING : HIP_SWING;
    const shoulderSwing = segmented ? SEGMENTED_SHOULDER_SWING : SHOULDER_SWING;
    const elbowBend = segmented ? SEGMENTED_ELBOW_BEND : ELBOW_BEND;
    const kneeScale = segmented ? SEGMENTED_KNEE_SCALE : 1;
    const pelvisDip = segmented ? SEGMENTED_PELVIS_DIP : PELVIS_DIP;

    // Cyclusfractie per been: 0 is de hielneerzet. De benen lopen precies een halve
    // cyclus uit fase.
    const tL = (this.#phase / TAU) % 1;
    const tR = (tL + 0.5) % 1;

    // Heup: maximaal naar voren bij de hielneerzet, maximaal naar achteren bij de afzet.
    // Naar voren is een negatieve rotatie, vandaar het minteken.
    joints.hipL.rotation.x = -Math.cos(TAU * tL) * hipSwing * intensity;
    joints.hipR.rotation.x = -Math.cos(TAU * tR) * hipSwing * intensity;

    // Knie: positief, dus de hiel gaat naar de bil. Groot in de zwaaifase zodat de voet
    // de grond vrij houdt, bijna gestrekt op het moment dat de hiel neerkomt.
    joints.kneeL.rotation.x = kneeFlexion(tL) * kneeScale * intensity;
    joints.kneeR.rotation.x = kneeFlexion(tR) * kneeScale * intensity;

    // Armen zwaaien tegengesteld aan het been aan dezelfde kant, maar minder ver: bij het
    // richten neemt de aim-pose het straks over.
    const armDamping = 1 - this.#aimBlend * 0.85;
    const armL = Math.cos(TAU * tL);
    const armR = Math.cos(TAU * tR);
    joints.shoulderL.rotation.x = armL * shoulderSwing * intensity * armDamping;
    joints.shoulderR.rotation.x = armR * shoulderSwing * intensity * armDamping;
    joints.shoulderL.rotation.z = (segmented ? 0.08 : 0) * armDamping;
    joints.shoulderR.rotation.z = (segmented ? -0.08 : 0) * armDamping;
    // De elleboog buigt als de arm naar voren komt, en naar voren is voor een elleboog de
    // negatieve richting.
    joints.elbowL.rotation.x = -Math.max(0, -armL) * elbowBend * intensity * armDamping;
    joints.elbowR.rotation.x = -Math.max(0, -armR) * elbowBend * intensity * armDamping;

    // Bekken: twee pieken per cyclus, hoogst midden in elke standfase en laagst bij de
    // dubbele steun. Zonder enkelgewricht kan het been niet afrollen, dus dit is een
    // benadering — hij houdt de voeten wel netjes boven de grond.
    const lift = 0.5 * (1 + Math.cos(4 * Math.PI * (tL - 0.3)));
    joints.pelvis.position.y =
      this.rig.spec.proportions.hipHeight +
      (segmented ? SEGMENTED_FOOT_CLEARANCE * intensity : 0) -
      pelvisDip * intensity * (1 - lift);

    // Romp kantelt mee met de pas — subtiel, maar het haalt de stijfheid eruit.
    joints.torso.rotation.y = -Math.cos(TAU * tL) * 0.07 * intensity * (1 - this.#aimBlend);
    joints.torso.rotation.z = -Math.cos(TAU * tL) * 0.02 * intensity;
    // Hoofd blijft op de weg gericht en compenseert de rompdraai een beetje.
    joints.head.rotation.x = HEAD_REST_TILT;
    joints.head.rotation.y = -joints.torso.rotation.y * 0.5;
  }

  #applyIdle(): void {
    const { joints } = this.rig;
    const breath = Math.sin(this.#phase * 0.35);

    joints.hipL.rotation.x = 0;
    joints.hipR.rotation.x = 0;
    joints.kneeL.rotation.x = 0;
    joints.kneeR.rotation.x = 0;

    const armDamping = 1 - this.#aimBlend;
    joints.shoulderL.rotation.x = breath * 0.03 * armDamping;
    joints.shoulderR.rotation.x = -breath * 0.03 * armDamping;
    // Positieve Z-rotatie duwt de onderkant van de linkerarm naar buiten; rechts is dat
    // negatief. De oude tekens waren omgekeerd en knepen de armen de buik in.
    // De procedurele rig heeft geen huid die meebuigt, dus de rusthouding komt uit deze
    // hoeken: armen iets van het lijf, elleboog licht gebogen — zoals op de character
    // sheet, niet als een pop met stokarmen.
    const segmented = this.rig.deformation === 'segmented';
    const shoulderOut = segmented ? 0.08 : 0.06;
    const idleElbow = segmented ? 0.14 : 0.12;
    joints.shoulderL.rotation.z = shoulderOut * armDamping;
    joints.shoulderR.rotation.z = -shoulderOut * armDamping;
    joints.elbowL.rotation.x = -idleElbow * armDamping;
    joints.elbowR.rotation.x = -idleElbow * armDamping;

    joints.pelvis.position.y = this.rig.spec.proportions.hipHeight + breath * 0.008;
    joints.torso.rotation.y = 0;
    joints.torso.rotation.z = 0;
    // Hoofd iets naar voren en licht gedraaid: niemand staat kaarsrecht recht vooruit te
    // kijken. Klein genoeg om de kijkrichting (+Z) niet te vervuilen.
    joints.head.rotation.x = HEAD_REST_TILT + breath * 0.01;
    joints.head.rotation.y = HEAD_REST_TURN * (1 - this.#aimBlend);
  }

  /**
   * Richtpose: de rechterarm komt horizontaal naar voren, de linker steunt half, en de
   * romp draait iets mee. Blendt in en uit met `#aimBlend` zodat de overgang niet knapt.
   */
  #applyAim(aimPitch: number): void {
    const blend = this.#aimBlend;
    if (blend < 0.001) return;

    const { joints } = this.rig;
    const skinned = this.rig.deformation === 'skinned';
    const rightShoulder = skinned ? -0.82 + aimPitch * 0.45 : -0.54 + aimPitch * 0.22;
    const leftShoulder = skinned ? -0.48 + aimPitch * 0.30 : -0.34 + aimPitch * 0.18;
    const rightElbow = skinned ? -0.62 : -0.24;
    const leftElbow = skinned ? -0.72 : -0.3;
    const rightShoulderZ = skinned ? 0.06 : -0.08;
    const leftShoulderZ = skinned ? -0.18 : 0.14;
    const torsoTurn = skinned ? -0.10 : -0.1;

    joints.shoulderR.rotation.x = THREE.MathUtils.lerp(joints.shoulderR.rotation.x, rightShoulder, blend);
    joints.shoulderR.rotation.z = THREE.MathUtils.lerp(joints.shoulderR.rotation.z, rightShoulderZ, blend);
    joints.elbowR.rotation.x = THREE.MathUtils.lerp(joints.elbowR.rotation.x, rightElbow, blend);

    joints.shoulderL.rotation.x = THREE.MathUtils.lerp(
      joints.shoulderL.rotation.x,
      leftShoulder,
      blend,
    );
    joints.shoulderL.rotation.z = THREE.MathUtils.lerp(joints.shoulderL.rotation.z, leftShoulderZ, blend);
    joints.elbowL.rotation.x = THREE.MathUtils.lerp(joints.elbowL.rotation.x, leftElbow, blend);

    // Schouders iets naar het doel toe: een gesloten, gerichte houding.
    joints.torso.rotation.y = THREE.MathUtils.lerp(joints.torso.rotation.y, torsoTurn, blend);
  }
}
